import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Modal Management Context
 *
 * Provides a unified system for managing all modals in the React home app.
 * Replaces Bootstrap modals with clean React Portal-based modals.
 */
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
/**
 * Hook to manage modal state with browser history integration.
 * When a modal opens, it pushes a history state so the back button can close it.
 * When closed by other means, the history is cleaned up.
 */
function useModalHistory({ historyKey }) {
    const [isOpen, setIsOpen] = useState(false);
    const historyPushedRef = useRef(false);
    const open = useCallback((replaceHistory = false) => {
        setIsOpen(true);
        const state = { [historyKey]: true };
        if (replaceHistory) {
            window.history.replaceState(state, '');
        }
        else {
            window.history.pushState(state, '');
        }
        historyPushedRef.current = true;
    }, [historyKey]);
    const close = useCallback((viaBackButton = false) => {
        setIsOpen(false);
        if (historyPushedRef.current && !viaBackButton) {
            historyPushedRef.current = false;
            if (window.history.state?.[historyKey]) {
                window.history.back();
            }
        }
        else {
            historyPushedRef.current = false;
        }
    }, [historyKey]);
    // Listen for browser back button
    useEffect(() => {
        const handlePopState = (event) => {
            if (isOpen && !event.state?.[historyKey]) {
                close(true);
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isOpen, historyKey, close]);
    return { isOpen, open, close };
}
const ModalContext = createContext(null);
export const useModals = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModals must be used within a ModalProvider');
    }
    return context;
};
export const ModalProvider = ({ children }) => {
    const [isUploadsModalOpen, setIsUploadsModalOpen] = useState(false);
    const [isDuctViewModalOpen, setIsDuctViewModalOpen] = useState(false);
    const [isDuctSearchModalOpen, setIsDuctSearchModalOpen] = useState(false);
    const [isPolygonPopupOpen, setIsPolygonPopupOpen] = useState(false);
    const [polygonPopupData, setPolygonPopupData] = useState(null);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogData, setConfirmDialogData] = useState(null);
    const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
    const [infoDialogData, setInfoDialogData] = useState(null);
    const [isConduitCreateModalOpen, setIsConduitCreateModalOpen] = useState(false);
    const [conduitCreateData, setConduitCreateData] = useState(null);
    // Uploads Modal
    const openUploadsModal = useCallback(() => {
        setIsUploadsModalOpen(true);
    }, []);
    const closeUploadsModal = useCallback(() => {
        setIsUploadsModalOpen(false);
    }, []);
    // Modals with browser history support (back button closes them)
    const layerSelectorModal = useModalHistory({ historyKey: 'layerSelectorModalOpen' });
    const routesModal = useModalHistory({ historyKey: 'routesModalOpen' });
    const routeEditorModal = useModalHistory({ historyKey: 'routeEditorModalOpen' });
    // Duct View Modal
    const openDuctViewModal = useCallback(() => {
        setIsDuctViewModalOpen(true);
    }, []);
    const closeDuctViewModal = useCallback(() => {
        setIsDuctViewModalOpen(false);
    }, []);
    // Duct Search Modal
    const openDuctSearchModal = useCallback(() => {
        setIsDuctSearchModalOpen(true);
    }, []);
    const closeDuctSearchModal = useCallback(() => {
        setIsDuctSearchModalOpen(false);
    }, []);
    // Polygon Popup
    const openPolygonPopup = useCallback((data) => {
        setPolygonPopupData(data);
        setIsPolygonPopupOpen(true);
    }, []);
    const closePolygonPopup = useCallback(() => {
        setIsPolygonPopupOpen(false);
        setPolygonPopupData(null);
    }, []);
    // Confirm Dialog
    const openConfirmDialog = useCallback((data) => {
        setConfirmDialogData(data);
        setIsConfirmDialogOpen(true);
    }, []);
    const closeConfirmDialog = useCallback(() => {
        setIsConfirmDialogOpen(false);
        setConfirmDialogData(null);
    }, []);
    // Info Dialog
    const openInfoDialog = useCallback((data) => {
        setInfoDialogData(data);
        setIsInfoDialogOpen(true);
    }, []);
    const closeInfoDialog = useCallback(() => {
        setIsInfoDialogOpen(false);
        setInfoDialogData(null);
    }, []);
    // Conduit Create Modal
    const openConduitCreateModal = useCallback((data) => {
        setConduitCreateData(data);
        setIsConduitCreateModalOpen(true);
    }, []);
    const closeConduitCreateModal = useCallback(() => {
        setIsConduitCreateModalOpen(false);
        setConduitCreateData(null);
    }, []);
    const value = {
        isUploadsModalOpen,
        openUploadsModal,
        closeUploadsModal,
        isLayerSelectorModalOpen: layerSelectorModal.isOpen,
        openLayerSelectorModal: layerSelectorModal.open,
        closeLayerSelectorModal: layerSelectorModal.close,
        isRoutesModalOpen: routesModal.isOpen,
        openRoutesModal: routesModal.open,
        closeRoutesModal: routesModal.close,
        isDuctViewModalOpen,
        openDuctViewModal,
        closeDuctViewModal,
        isDuctSearchModalOpen,
        openDuctSearchModal,
        closeDuctSearchModal,
        isRouteEditorModalOpen: routeEditorModal.isOpen,
        openRouteEditorModal: routeEditorModal.open,
        closeRouteEditorModal: routeEditorModal.close,
        isPolygonPopupOpen,
        polygonPopupData,
        openPolygonPopup,
        closePolygonPopup,
        isConfirmDialogOpen,
        confirmDialogData,
        openConfirmDialog,
        closeConfirmDialog,
        isInfoDialogOpen,
        infoDialogData,
        openInfoDialog,
        closeInfoDialog,
        isConduitCreateModalOpen,
        conduitCreateData,
        openConduitCreateModal,
        closeConduitCreateModal,
    };
    return (_jsx(ModalContext.Provider, { value: value, children: children }));
};
